import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function DonatePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.masdeuna.org';
  
  useSEO({
    title: 'Donate to MasDeUna - Support Free Latina Leadership Development',
    description: 'Donate from $10 to a custom amount to help keep MasDeUna free. Self-development should not have a cost of entry. Support mentorship, professional development workshops, and economic mobility for Latinas.',
    keywords: 'donate, support Latina leadership, nonprofit donation, mentorship funding, professional development, MasDeUna',
    canonical: `${siteUrl}/donate`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'DonateAction',
      name: 'Donate to MasDeUna',
      url: `${siteUrl}/donate`,
      recipient: {
        '@type': 'Organization',
        name: 'MasDeUna',
        url: siteUrl
      },
      description: 'Support free professional development and mentorship for Latina leaders. Donations help keep MasDeUna accessible to all.'
    }
  });

  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [frequency, setFrequency] = useState<'one-time' | 'monthly'>('one-time');

  const predefinedAmounts = [10, 25, 50, 100, 250, 500];

  const handleDonate = () => {
    const amount = selectedAmount || parseFloat(customAmount);
    if (amount && amount > 0) {
      // This would integrate with your payment processor
      alert(`Thank you for your ${frequency} donation of $${amount}!`);
    } else {
      alert('Please select or enter a donation amount.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#E6007E] to-[#c00069]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <h1 className="font-['Poppins'] font-bold text-6xl md:text-7xl lg:text-8xl text-white mb-6 text-center">
            Fuel the Movement
          </h1>
          <p className="font-['Inter'] text-xl md:text-2xl text-white/95 text-center max-w-3xl mx-auto mb-4">
            Donate from $10 to a custom amount to help keep MasDeUna free
          </p>
          <p className="font-['Inter'] text-xl md:text-2xl text-white/95 text-center max-w-3xl mx-auto font-semibold">
            Self-development should not have a cost of entry.
          </p>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#111111] mb-12 text-center">
              Your Impact
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="text-center p-8 bg-[#FFC72C]/10 rounded-2xl">
                <div className="text-5xl font-bold text-[#E6007E] mb-4">$10</div>
                <p className="font-['Inter'] text-lg text-gray-700">
                  Helps provide resources for mentee sessions
                </p>
              </div>
              
              <div className="text-center p-8 bg-[#1E90FF]/10 rounded-2xl">
                <div className="text-5xl font-bold text-[#E6007E] mb-4">$100</div>
                <p className="font-['Inter'] text-lg text-gray-700">
                  Sponsors a professional development workshop
                </p>
              </div>
              
              <div className="text-center p-8 bg-[#FFC72C]/10 rounded-2xl">
                <div className="text-5xl font-bold text-[#E6007E] mb-4">$500</div>
                <p className="font-['Inter'] text-lg text-gray-700">
                  Funds a full MAS Academy workshop for one Latina
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-3xl p-12 shadow-xl">
              <h3 className="font-['Poppins'] font-bold text-3xl text-[#111111] mb-8 text-center">
                Sponsor a Workshop
              </h3>

              {/* Frequency Toggle */}
              <div className="flex justify-center mb-8">
                <div className="inline-flex bg-white rounded-full p-1 shadow-md">
                  <button
                    onClick={() => setFrequency('one-time')}
                    className={`px-8 py-3 rounded-full font-['Poppins'] font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      frequency === 'one-time'
                        ? 'bg-[#E6007E] text-white'
                        : 'text-gray-600 hover:text-[#E6007E]'
                    }`}
                  >
                    One-Time
                  </button>
                  <button
                    onClick={() => setFrequency('monthly')}
                    className={`px-8 py-3 rounded-full font-['Poppons'] font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      frequency === 'monthly'
                        ? 'bg-[#E6007E] text-white'
                        : 'text-gray-600 hover:text-[#E6007E]'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* Amount Selection */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {predefinedAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={`p-6 rounded-xl font-['Poppins'] font-bold text-2xl transition-all duration-300 border-2 whitespace-nowrap cursor-pointer ${
                      selectedAmount === amount
                        ? 'bg-[#E6007E] text-white border-[#E6007E] shadow-lg scale-105'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#E6007E] hover:text-[#E6007E]'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="mb-8">
                <label className="block font-['Poppins'] font-semibold text-lg text-gray-700 mb-3">
                  Or enter a custom amount:
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-500 font-bold">
                    $
                  </span>
                  <input
                    type="number"
                    min="1"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    placeholder="Enter amount"
                    className="w-full pl-12 pr-6 py-4 text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-[#E6007E] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Donate Button */}
              <button
                onClick={handleDonate}
                className="w-full py-5 bg-[#E6007E] text-white font-['Poppins'] font-bold text-xl rounded-xl hover:bg-[#c00069] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Donate {frequency === 'monthly' ? 'Monthly' : 'Now'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="py-20 bg-[#111111] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl mb-12 text-center">
              Why Your Support Matters
            </h2>
            
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#E6007E] rounded-full flex items-center justify-center">
                  <i className="ri-group-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="font-['Poppins'] font-bold text-2xl mb-3">Breaking Barriers</h3>
                  <p className="font-['Inter'] text-lg text-white/80 leading-relaxed">
                    Only 2% of C-suite executives are Latinas. Your donation helps us change this statistic by providing mentorship and professional development opportunities.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#FFC72C] rounded-full flex items-center justify-center">
                  <i className="ri-book-open-line text-2xl text-[#111111]"></i>
                </div>
                <div>
                  <h3 className="font-['Poppins'] font-bold text-2xl mb-3">Education Access</h3>
                  <p className="font-['Inter'] text-lg text-white/80 leading-relaxed">
                    Financial barriers should never prevent Latinas from accessing professional development. MAS Academy provides free courses thanks to donors like you.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#1E90FF] rounded-full flex items-center justify-center">
                  <i className="ri-rocket-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="font-['Poppins'] font-bold text-2xl mb-3">Lasting Impact</h3>
                  <p className="font-['Inter'] text-lg text-white/80 leading-relaxed">
                    When you invest in one Latina leader, you create a ripple effect. She will mentor others, creating a legacy of empowerment for generations to come.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative Ways to Give */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#111111] mb-12">
              Other Ways to Support
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-8 bg-gray-50 rounded-2xl">
                <i className="ri-briefcase-line text-5xl text-[#E6007E] mb-4"></i>
                <h3 className="font-['Poppins'] font-bold text-2xl mb-4">Corporate Sponsorship</h3>
                <p className="font-['Inter'] text-lg text-gray-700 mb-6">
                  Partner with us to sponsor workshops, events, or full academy courses.
                </p>
                <a
                  href="/contact-us"
                  className="inline-block px-6 py-3 border-2 border-[#E6007E] text-[#E6007E] font-['Poppins'] font-semibold rounded-lg hover:bg-[#E6007E] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Learn More
                </a>
              </div>

              <div className="p-8 bg-gray-50 rounded-2xl">
                <i className="ri-time-line text-5xl text-[#FFC72C] mb-4"></i>
                <h3 className="font-['Poppins'] font-bold text-2xl mb-4">Volunteer Your Time</h3>
                <p className="font-['Inter'] text-lg text-gray-700 mb-6">
                  Become a mentor and invest 60 minutes to transform a Latina's career trajectory.
                </p>
                <a
                  href="https://forms.gle/iQmn9h9gtcXQJePe8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 border-2 border-[#FFC72C] text-[#FFC72C] font-['Poppins'] font-semibold rounded-lg hover:bg-[#FFC72C] hover:text-[#111111] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Sign Up
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
